// Adds included items, such as nic shots with shortfills, to the cart as nested lines under the
// product they come with, and keeps their quantity in step with it. The ECD Included Items app's
// discounts make them 100% off.
//
// Some included items are the customer's pick, such as the e-liquid with a kit. The product page's
// picker (assets/included-items-picker.js) says what was picked with `remember`, before the product
// form adds the kit. A kit whose quantity goes up gets more of the e-liquid it already has. There's
// no picker in the cart: a kit whose e-liquid the customer removed stays without, like a shortfill
// without its nic shot.
//
// It runs after every cart update (whatever made it: the product form, quick add, the cart's
// quantity buttons) and on page load. sections/included-items.liquid works out what each line
// includes, so the rules live in one place (a shop metafield the app saves).
//
// Shots a customer removes stay removed until the shortfill's quantity goes up: each line's quantity
// when this last checked it is kept in the hidden cart attribute `_included_seen`, as
// "variant=quantity,variant=quantity". Not JSON: Liquid HTML-escapes attribute values, quotes
// included. By variant, not line key: a line's key changes when, say, a multibuy starts applying.
(() => {
  if (window.includedItemsLoaded) return;
  window.includedItemsLoaded = true;

  const SOURCE = 'included-items';
  const PICKS_KEY = 'included-items-picks';
  // A pick is for the product being added now; one left over from a failed add soon lapses.
  const PICK_LIFETIME = 10 * 60 * 1000;
  const root = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();

  // Picks made on a product page, by the product's variant: {variant: {picked, at}}. Kept for the
  // session too, as the product form goes to the cart page when the cart drawer is off.
  let picks = loadPicks();

  function loadPicks() {
    try {
      return JSON.parse(sessionStorage.getItem(PICKS_KEY)) || {};
    } catch {
      return {};
    }
  }

  function savePicks() {
    try {
      sessionStorage.setItem(PICKS_KEY, JSON.stringify(picks));
    } catch {}
  }

  function takePick(variant) {
    const pick = picks[variant];
    if (!pick) return null;
    delete picks[variant];
    savePicks();
    return Date.now() - pick.at < PICK_LIFETIME ? pick.picked : null;
  }

  const post = (url, body) =>
    fetch(url, {...fetchConfig(), body: JSON.stringify(body)}).then((response) => response.json());

  async function readCart() {
    // Never from the browser's cache: a stale copy would show items that have since been removed.
    const response = await fetch(`${root}?sections=included-items&t=${Date.now()}`, {cache: 'no-store'}).then((r) =>
      r.json(),
    );
    const html = new DOMParser().parseFromString(response['included-items'], 'text/html');
    const data = JSON.parse(html.querySelector('[data-included-items]').textContent);
    data.seen = parseSeen(data.seen);
    return data;
  }

  function parseSeen(value) {
    const seen = {};
    for (const entry of (value || '').split(',')) {
      const [variant, quantity] = entry.split('=');
      if (variant && quantity) seen[variant] = Number(quantity);
    }
    return seen;
  }

  const formatSeen = (seen) =>
    Object.entries(seen)
      .map(([variant, quantity]) => `${variant}=${quantity}`)
      .join(',');

  function sectionsToRender() {
    const sections = [{id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section'}];
    const drawer = document.getElementById('Cart-Drawer');
    if (drawer) sections.push({id: 'Cart-Drawer', section: drawer.dataset.section, selector: '#Cart-Drawer-Details'});
    const page = document.getElementById('Main-Cart');
    if (page) sections.push({id: 'Main-Cart', section: page.dataset.section, selector: '#Main-Cart-Details'});
    return sections;
  }

  // Which variant to add to a line short of included items, if any. `siblings` are the included items
  // under any line of the same variant, as Shopify can split a product into several lines when a
  // discount covers only some of its units, leaving the new line with none of its own.
  function topUpWith(parent, children, siblings, totals, seen) {
    const {variants} = parent.includes;
    // `seen` is by variant, so compare the variant's total over all its lines.
    const grew = totals[parent.variant] > (seen[parent.variant] || 0);
    // A set item: whenever the line is new or the customer added more.
    if (variants) return grew ? variants[0] : null;
    // A pick: one the customer just made, or more of the one the product already has.
    const picked = grew ? takePick(parent.variant) : null;
    if (picked) return picked;
    const copy = children.at(-1) ?? siblings.at(-1);
    return grew && copy ? copy.variant : null;
  }

  // What to add and remove so each line has its included items, given what the cart holds now.
  function plan({lines, seen}) {
    const adds = [];
    const updates = {};
    const nextSeen = {};
    const totals = {};
    for (const line of lines) {
      if (line.includes) totals[line.variant] = (totals[line.variant] || 0) + line.quantity;
    }

    for (const parent of lines) {
      if (!parent.includes) continue;
      const {variants, collections, quantityPerUnit} = parent.includes;
      const wanted = parent.quantity * quantityPerUnit;
      const isIncluded = (line) =>
        variants ? variants.includes(line.variant) : line.inPick.some((handle) => collections.includes(handle));

      // Shopify can split one nested line in two (the discounted units and the rest), so total
      // the quantities by line key. It can also merge two lines of the same product and drop the
      // parent of an item nested under the one that went, so a loose item marked as coming with this
      // product's SKU counts too.
      const belongs = (line) =>
        line.parent === parent.key || (!line.parent && parent.sku && line.includedWith === parent.sku);
      const childLines = lines.filter((line) => belongs(line) && isIncluded(line));
      const children = new Map();
      for (const line of childLines) children.set(line.key, (children.get(line.key) || 0) + line.quantity);
      const have = [...children.values()].reduce((sum, quantity) => sum + quantity, 0);

      const sameVariant = new Set(lines.filter((line) => line.variant === parent.variant).map((line) => line.key));
      const siblings = lines.filter((line) => sameVariant.has(line.parent) && isIncluded(line));
      const variant = have < wanted ? topUpWith(parent, childLines, siblings, totals, seen) : null;
      if (variant) {
        // New, or the customer added more: top up.
        // The hidden `_included_with` property carries the parent's SKU onto the order, so it's clear
        // which product each included item came with. The underscore keeps it out of checkout.
        const item = {id: variant, quantity: wanted - have, parent_line_key: parent.key};
        if (parent.sku) item.properties = {_included_with: parent.sku};
        adds.push(item);
      } else if (have > wanted) {
        // The customer took some away: trim the included items to match.
        let excess = have - wanted;
        for (const [key, quantity] of [...children].reverse()) {
          const remove = Math.min(quantity, excess);
          updates[key] = quantity - remove;
          excess -= remove;
          if (!excess) break;
        }
      }
      nextSeen[parent.variant] = (nextSeen[parent.variant] || 0) + parent.quantity;
    }

    const seenChanged = formatSeen(nextSeen) !== formatSeen(seen);
    return {adds, updates, nextSeen, changed: adds.length > 0 || Object.keys(updates).length > 0 || seenChanged};
  }

  async function reconcile() {
    const cart = await readCart();
    if (!cart.enabled) return;
    const {adds, updates, nextSeen, changed} = plan(cart);
    if (!changed) return;

    // One at a time, so an item that's out of stock doesn't stop the others.
    for (const item of adds) await post(window.routes.cart_add_url, {items: [item]}).catch(() => {});

    const sections = sectionsToRender();
    const response = await post(window.routes.cart_update_url, {
      updates,
      attributes: {_included_seen: formatSeen(nextSeen)},
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });
    if (response.status) return;

    SectionDynamicUpdate.updateSections(sections, response.sections);
    publish(PUB_SUB_EVENTS.cartUpdate, {source: SOURCE, cartData: response});
  }

  const schedule = () => {
    queue = queue.then(reconcile).catch(() => {});
  };

  window.includedItems = {
    remember(variant, picked) {
      picks[variant] = {picked, at: Date.now()};
      savePicks();
    },
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source !== SOURCE) schedule();
  });
  if (Number(document.currentScript?.dataset.itemCount) > 0) schedule();
})();
